import { type DeviceCardDto, type DeviceDto, INSTALLATION_STATUSES, type InstallationDto, type MeDto, PLAN_ANCHORS, PROTOCOL_DELIVERY_STATES, PROTOCOL_KINDS, type ServicePlanDto } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Card, Checkbox, Group, Modal, NumberInput, SegmentedControl, Select, Stack, Table, Tabs, Text, TextInput, Timeline } from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { CompanyBadgeById, TradeBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { EmptyState } from '../../components/ui';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { deliveryColor } from '../planning/ProtocolsBlock';
import { dateOnly, DeviceStatusBadge, HealthBadge, LateInspections, todayIso, WarrantyRisk } from './common';
import { minHint } from '../../minHint';

/** Instalacje i karty urządzeń — wyszukiwanie po numerze seryjnym, adresie, kliencie, numerze sprawy. */
export function InstallationsPage({ me }: { me: MeDto }) {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [device, setDevice] = useState<string | null>(null);
  const [adding, setAdding] = useState<InstallationDto | null>(null);
  const one = params.get('id');
  const list = useQuery({
    queryKey: ['installations', one ?? q],
    queryFn: async () => (one ? [await api<InstallationDto>(`/installations/${one}`)] : api<InstallationDto[]>(`/installations?q=${encodeURIComponent(q)}`)),
  });
  const canAdd = can(me, 'urzadzenia.dodawanie', 'W') || can(me, 'urzadzenia.edycja', 'W');

  return (
    <>
      <PageHeader
        title="Instalacje i urządzenia"
        description="Instalacje powstają z zakresów prac przy pierwszym protokole (urządzenia z numerami seryjnymi z aplikacji mobilnej) albo ręcznie. Karta urządzenia: gwarancja, plan przeglądów, historia, wymiana."
      />
      <Group mb="md" wrap="wrap">
        <TextInput
          leftSection={<IconSearch size={16} />}
          placeholder="Numer seryjny, adres, klient, numer sprawy"
          value={q}
          onChange={(e) => {
            setQ(e.currentTarget.value);
            if (one) setParams({});
          }}
          w={{ base: '100%', sm: 360 }}
        />
        {one && (
          <Button variant="subtle" onClick={() => setParams({})}>
            Pokaż wszystkie
          </Button>
        )}
      </Group>
      <QueryState isLoading={list.isLoading} error={list.error}>
        <Stack>
          {list.data?.length === 0 && (
            <Card withBorder>
              <EmptyState title="Brak instalacji" hint={q ? 'Nic nie pasuje do wyszukiwania — zmień numer, adres albo klienta.' : 'Instalacje powstają przy pierwszym protokole z realizacji.'} />
            </Card>
          )}
          {list.data?.map((i) => (
            <Card key={i.id} withBorder>
              <Group justify="space-between" align="flex-start">
                <div>
                  <Group gap={6}>
                    <TradeBadge code={i.tradeCode} />
                    <Text fw={700}>{i.name}</Text>
                    <Badge variant="light" color={i.status === 'czynna' ? 'green' : 'gray'}>
                      {INSTALLATION_STATUSES[i.status]}
                    </Badge>
                    {i.companyId && <CompanyBadgeById id={i.companyId} />}
                  </Group>
                  <Text size="sm">
                    {i.clientName ?? '—'} · {i.siteAddress}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {i.commissionedOn ? `uruchomiona ${dateOnly(i.commissionedOn)}` : 'nieuruchomiona'}
                    {i.sourceCaseId && (
                      <>
                        {' · sprawa '}
                        <Anchor component={Link} to={`/sprawy/${i.sourceCaseId}?zakladka=prace`} size="xs">
                          {i.sourceCaseNumber}
                        </Anchor>
                      </>
                    )}
                  </Text>
                </div>
                {canAdd && (
                  <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setAdding(i)}>
                    Dodaj urządzenie
                  </Button>
                )}
              </Group>
              {i.devices.length > 0 && (
                <Table.ScrollContainer minWidth={560} mt="xs">
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Urządzenie</Table.Th>
                        <Table.Th>Numer seryjny</Table.Th>
                        <Table.Th>Gwarancja</Table.Th>
                        <Table.Th>Stan</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {i.devices.map((d) => (
                        <Table.Tr key={d.id} className="row-link" style={{ opacity: d.status === 'czynne' ? 1 : 0.6 }} onClick={() => setDevice(d.id)}>
                          <Table.Td>
                            <Text size="sm" fw={600}>
                              {d.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {[d.manufacturer, d.model].filter(Boolean).join(' ')}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" ff="monospace">
                              {d.serialNumber || '— brak SN —'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">do {dateOnly(d.warrantyUntil)}</Text>
                          </Table.Td>
                          <Table.Td>
                            <DeviceStatusBadge status={d.status} />
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
              {i.devices.length === 0 && <EmptyState compact title="Brak urządzeń" />}
              {i.plans.length > 0 && (
                <Group gap="xs" mt="xs">
                  <Text size="xs" c="dimmed">
                    Przeglądy:
                  </Text>
                  {i.plans.map((p) => (
                    <Group key={p.id} gap={4}>
                      <Text size="xs">{dateOnly(p.due)}</Text>
                      <HealthBadge health={p.health} />
                    </Group>
                  ))}
                </Group>
              )}
              <LateInspections items={i.plans.flatMap((p) => p.lateInspections ?? []).sort((a, b) => b.doneOn.localeCompare(a.doneOn))} max={2} />
            </Card>
          ))}
        </Stack>
      </QueryState>
      <Modal opened={!!device} onClose={() => setDevice(null)} title="Karta urządzenia" size="xl">
        {device && <DeviceCard id={device} onOpen={setDevice} />}
      </Modal>
      <Modal opened={!!adding} onClose={() => setAdding(null)} title={`Nowe urządzenie — ${adding?.name ?? ''}`}>
        {adding && <DeviceForm installationId={adding.id} onDone={() => setAdding(null)} />}
      </Modal>
    </>
  );
}

type DeviceFields = Pick<DeviceDto, 'name' | 'manufacturer' | 'model' | 'serialNumber' | 'installedOn' | 'commissionedOn' | 'warrantyUntil' | 'warrantyRequiresService' | 'notes'>;
const emptyDevice: DeviceFields = { name: '', manufacturer: '', model: '', serialNumber: '', installedOn: null, commissionedOn: null, warrantyUntil: null, warrantyRequiresService: false, notes: '' };

function DeviceFieldsForm({ f, set }: { f: DeviceFields; set: (f: DeviceFields) => void }) {
  const d = (k: 'installedOn' | 'commissionedOn' | 'warrantyUntil', label: string) => <TextInput type="date" label={label} value={f[k] ?? ''} onChange={(e) => set({ ...f, [k]: e.currentTarget.value || null })} />;
  return (
    <>
      <TextInput label="Nazwa" value={f.name} onChange={(e) => set({ ...f, name: e.currentTarget.value })} />
      <Group grow>
        <TextInput label="Producent" value={f.manufacturer} onChange={(e) => set({ ...f, manufacturer: e.currentTarget.value })} />
        <TextInput label="Model" value={f.model} onChange={(e) => set({ ...f, model: e.currentTarget.value })} />
      </Group>
      <TextInput label="Numer seryjny" value={f.serialNumber} onChange={(e) => set({ ...f, serialNumber: e.currentTarget.value })} />
      <Group grow>
        {d('installedOn', 'Data montażu')}
        {d('commissionedOn', 'Data uruchomienia')}
        {d('warrantyUntil', 'Gwarancja do')}
      </Group>
      <Checkbox label="Gwarancja wymaga przeglądów okresowych" checked={f.warrantyRequiresService} onChange={(e) => set({ ...f, warrantyRequiresService: e.currentTarget.checked })} />
      <TextInput label="Uwagi" value={f.notes} onChange={(e) => set({ ...f, notes: e.currentTarget.value })} />
    </>
  );
}

function DeviceForm({ installationId, onDone }: { installationId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<DeviceFields>(emptyDevice);
  const save = useMutation({
    mutationFn: () => api('/devices', { method: 'POST', body: { ...f, installationId } }),
    onSuccess: () => (notifyOk('Dodano urządzenie'), void qc.invalidateQueries({ queryKey: ['installations'] }), onDone()),
    onError: notifyError,
  });
  return (
    <Stack>
      <DeviceFieldsForm f={f} set={setF} />
      <Group justify="flex-end">
        <Button variant="default" onClick={onDone}>
          Anuluj
        </Button>
        <Button disabled={!f.name.trim()} loading={save.isPending} onClick={() => save.mutate()}>
          Dodaj urządzenie
        </Button>
      </Group>
    </Stack>
  );
}

function DeviceCard({ id, onOpen }: { id: string; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const card = useQuery({ queryKey: ['device', id], queryFn: () => api<DeviceCardDto>(`/devices/${id}`) });
  const [tab, setTab] = useState<string | null>('karta');
  const [edit, setEdit] = useState<(DeviceFields & { reason: string }) | null>(null);
  const [replace, setReplace] = useState<{ date: string; reason: string; name: string; manufacturer: string; model: string; serialNumber: string; movePlans: boolean } | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['device'] });
    void qc.invalidateQueries({ queryKey: ['installations'] });
    void qc.invalidateQueries({ queryKey: ['service-plans'] });
  };
  const saveEdit = useMutation({ mutationFn: () => api(`/devices/${id}`, { method: 'PUT', body: edit }), onSuccess: () => (notifyOk('Zapisano kartę urządzenia'), setEdit(null), refresh()), onError: notifyError });
  const doReplace = useMutation({
    mutationFn: () => api<DeviceDto>(`/devices/${id}/replace`, { method: 'POST', body: replace }),
    onSuccess: (n) => (notifyOk('Urządzenie wymienione — otwarto kartę nowego urządzenia'), setReplace(null), refresh(), onOpen(n.id)),
    onError: notifyError,
  });
  const c = card.data;
  return (
    <QueryState isLoading={card.isLoading} error={card.error}>
      {c && (
        <Stack gap="sm">
          <Group justify="space-between">
            <div>
              <Text fw={700} size="lg">
                {c.device.name}
              </Text>
              <Text size="sm" c="dimmed">
                {[c.device.manufacturer, c.device.model].filter(Boolean).join(' ')} · SN <span style={{ fontFamily: 'monospace' }}>{c.device.serialNumber || '—'}</span>
              </Text>
              <Text size="sm">
                {c.installation.name} · {c.installation.clientName ?? '—'} · {c.installation.siteAddress}
              </Text>
            </div>
            <DeviceStatusBadge status={c.device.status} />
          </Group>
          {c.replacedBy && (
            <Alert color="gray">
              Wymienione {dateOnly(c.device.replacedOn)} ({c.device.replaceReason}) na{' '}
              <Anchor component="button" type="button" onClick={() => onOpen(c.replacedBy!.id)}>
                {c.replacedBy.name} {c.replacedBy.serialNumber}
              </Anchor>
            </Alert>
          )}
          {c.replaces && (
            <Text size="sm">
              Zastępuje:{' '}
              <Anchor component="button" type="button" onClick={() => onOpen(c.replaces!.id)}>
                {c.replaces.name} {c.replaces.serialNumber}
              </Anchor>
            </Text>
          )}
          <Tabs value={tab} onChange={setTab}>
            <Tabs.List>
              <Tabs.Tab value="karta">Karta</Tabs.Tab>
              <Tabs.Tab value="przeglady">Przeglądy ({c.plans.length})</Tabs.Tab>
              <Tabs.Tab value="historia">Historia</Tabs.Tab>
            </Tabs.List>
          </Tabs>
          {tab === 'karta' &&
            (edit ? (
              <Stack gap="xs">
                <DeviceFieldsForm f={edit} set={(f) => setEdit({ ...edit, ...f })} />
                <TextInput label="Powód zmiany" required description={minHint(edit.reason)} value={edit.reason} onChange={(e) => setEdit({ ...edit, reason: e.currentTarget.value })} />
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setEdit(null)}>
                    Anuluj
                  </Button>
                  <Button disabled={edit.reason.trim().length < 3} loading={saveEdit.isPending} onClick={() => saveEdit.mutate()}>
                    Zapisz
                  </Button>
                </Group>
              </Stack>
            ) : replace ? (
              <Stack gap="xs">
                <Text size="sm">Stara karta zostaje z całą historią (stan „Wymienione”). Nowe urządzenie dostaje odnośnik „zastępuje”.</Text>
                <Group grow>
                  <TextInput type="date" label="Data wymiany" value={replace.date} onChange={(e) => setReplace({ ...replace, date: e.currentTarget.value })} />
                  <TextInput label="Powód" required description={minHint(replace.reason)} value={replace.reason} onChange={(e) => setReplace({ ...replace, reason: e.currentTarget.value })} />
                </Group>
                <TextInput label="Nowe urządzenie — nazwa" value={replace.name} onChange={(e) => setReplace({ ...replace, name: e.currentTarget.value })} />
                <Group grow>
                  <TextInput label="Producent" value={replace.manufacturer} onChange={(e) => setReplace({ ...replace, manufacturer: e.currentTarget.value })} />
                  <TextInput label="Model" value={replace.model} onChange={(e) => setReplace({ ...replace, model: e.currentTarget.value })} />
                  <TextInput label="Numer seryjny" value={replace.serialNumber} onChange={(e) => setReplace({ ...replace, serialNumber: e.currentTarget.value })} />
                </Group>
                <Checkbox label="Przenieś plan przeglądów na nowe urządzenie (termin od daty wymiany)" checked={replace.movePlans} onChange={(e) => setReplace({ ...replace, movePlans: e.currentTarget.checked })} />
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => setReplace(null)}>
                    Anuluj
                  </Button>
                  <Button disabled={replace.reason.trim().length < 3 || !replace.name.trim()} loading={doReplace.isPending} onClick={() => doReplace.mutate()}>
                    Wymień
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Stack gap={4}>
                <Text size="sm">Data montażu: {dateOnly(c.device.installedOn)}</Text>
                <Text size="sm">Data uruchomienia: {dateOnly(c.device.commissionedOn)}</Text>
                <Text size="sm">
                  Gwarancja do: <b>{dateOnly(c.device.warrantyUntil)}</b>
                  {c.device.warrantyRequiresService ? ' · wymaga przeglądów okresowych' : ''}
                </Text>
                {c.device.notes && <Text size="sm">Uwagi: {c.device.notes}</Text>}
                {c.can.edit && c.device.status === 'czynne' && (
                  <Group mt="xs">
                    <Button size="xs" variant="light" onClick={() => setEdit({ ...c.device, reason: '' })}>
                      Edytuj kartę
                    </Button>
                    <Button size="xs" variant="light" color="orange" onClick={() => setReplace({ date: todayIso(), reason: '', name: c.device.name, manufacturer: c.device.manufacturer, model: c.device.model, serialNumber: '', movePlans: true })}>
                      Wymień urządzenie
                    </Button>
                  </Group>
                )}
              </Stack>
            ))}
          {tab === 'przeglady' && <Plans card={c} onChange={refresh} />}
          {tab === 'historia' && (
            <>
              <Timeline bulletSize={14} lineWidth={2}>
                {c.history.map((h, i) => (
                  <Timeline.Item key={i} title={<Text size="sm">{h.label}</Text>}>
                    <Text size="xs" c="dimmed">
                      {h.at.length === 10 ? dateOnly(h.at) : formatDateTime(h.at)}
                    </Text>
                  </Timeline.Item>
                ))}
              </Timeline>
              {c.protocols.length > 0 && (
                <Stack gap={4} mt="sm">
                  <Text size="sm" fw={600}>
                    Protokoły instalacji
                  </Text>
                  {c.protocols.map((p) => (
                    <Group key={p.id} gap={6}>
                      <Text size="sm" td={p.status === 'uniewazniony' ? 'line-through' : undefined}>
                        {p.code} — {PROTOCOL_KINDS[p.kind]} ({p.taskNumber}), {formatDateTime(p.signedAt)}
                      </Text>
                      <Badge size="xs" color={deliveryColor(p.delivery)}>
                        {PROTOCOL_DELIVERY_STATES[p.delivery]}
                      </Badge>
                      <Anchor href={`/api/protocols/${p.id}/pdf`} target="_blank" size="xs">
                        PDF
                      </Anchor>
                    </Group>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Stack>
      )}
    </QueryState>
  );
}

type PlanAction = { plan: ServicePlanDto; action: 'cykl' | 'przesun' | 'wstrzymaj' | 'wznow' };

function Plans({ card, onChange }: { card: DeviceCardDto; onChange: () => void }) {
  const [act, setAct] = useState<PlanAction | null>(null);
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ anchor: 'wykonanie', cycleUnit: 'miesiace', cycleValue: 12, toleranceDays: 30, leadDays: 45, due: todayIso(), whole: false, reason: '', baseDate: card.device.commissionedOn ?? todayIso() });
  const run = useMutation({
    mutationFn: () =>
      creating
        ? api('/service-plans', { method: 'POST', body: { installationId: card.installation.id, deviceId: card.device.id, anchor: f.anchor, cycleUnit: f.cycleUnit, cycleValue: f.cycleValue, toleranceDays: f.toleranceDays, leadDays: f.leadDays, baseDate: f.baseDate } })
        : api(`/service-plans/${act!.plan.id}`, {
            method: 'PATCH',
            body:
              act!.action === 'cykl'
                ? { action: 'cykl', anchor: f.anchor, cycleUnit: f.cycleUnit, cycleValue: f.cycleValue, toleranceDays: f.toleranceDays, leadDays: f.leadDays, reason: f.reason }
                : act!.action === 'przesun'
                  ? { action: 'przesun', due: f.due, whole: f.whole, reason: f.reason }
                  : { action: act!.action, reason: f.reason },
          }),
    onSuccess: () => (notifyOk('Zapisano plan przeglądów'), setAct(null), setCreating(false), onChange()),
    onError: notifyError,
  });
  const open = (plan: ServicePlanDto, action: PlanAction['action']) => {
    setF({ ...f, anchor: plan.anchor, cycleUnit: plan.cycleUnit, cycleValue: plan.cycleValue, toleranceDays: plan.toleranceDays, leadDays: plan.leadDays, due: plan.due, whole: false, reason: '' });
    setAct({ plan, action });
  };
  const cycleFields = (
    <>
      <Select label="Kolejny termin liczony" data={Object.entries(PLAN_ANCHORS).map(([value, label]) => ({ value, label }))} value={f.anchor} onChange={(v) => v && setF({ ...f, anchor: v })} />
      <Group grow>
        <NumberInput label="Cykl" min={1} max={120} value={f.cycleValue} onChange={(v) => setF({ ...f, cycleValue: Number(v) || 1 })} />
        <SegmentedControl mt={24} value={f.cycleUnit} onChange={(v) => setF({ ...f, cycleUnit: v })} data={[{ value: 'miesiace', label: 'miesięcy' }, { value: 'dni', label: 'dni' }]} />
      </Group>
      <Group grow>
        <NumberInput label="Tolerancja ± dni" min={0} max={180} value={f.toleranceDays} onChange={(v) => setF({ ...f, toleranceDays: Number(v) || 0 })} />
        <NumberInput label="Zgłoszenie dni przed terminem" min={0} max={180} value={f.leadDays} onChange={(v) => setF({ ...f, leadDays: Number(v) || 0 })} />
      </Group>
    </>
  );
  return (
    <Stack gap="xs">
      {card.plans.map((p) => (
        <Card key={p.id} withBorder p="sm">
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <div>
              <Group gap={6}>
                <Text fw={600}>Termin: {dateOnly(p.due)}</Text>
                <HealthBadge health={p.health} />
              </Group>
              <Text size="xs" c="dimmed">
                co {p.cycleValue} {p.cycleUnit === 'miesiace' ? 'mies.' : 'dni'} · {PLAN_ANCHORS[p.anchor]} · tolerancja ±{p.toleranceDays} dni · ostatnio wykonany {dateOnly(p.lastDoneOn)}
                {p.deviceId ? '' : ' · plan całej instalacji'}
              </Text>
              {p.warrantyRequiresInspections && p.health === 'zalegly' && <WarrantyRisk />}
              <LateInspections items={p.lateInspections} />
              {!p.active && <Text size="xs">Wstrzymany: {p.pauseReason}</Text>}
              {p.openTicketId && (
                <Anchor component={Link} to={`/serwis?zgloszenie=${p.openTicketId}`} size="xs">
                  Otwarte zgłoszenie {p.openTicketNumber}
                </Anchor>
              )}
            </div>
            {card.can.plans && (
              <Stack gap={2}>
                <Button size="compact-xs" variant="subtle" onClick={() => open(p, 'przesun')}>
                  Przesuń termin
                </Button>
                <Button size="compact-xs" variant="subtle" onClick={() => open(p, 'cykl')}>
                  Zmień cykl
                </Button>
                <Button size="compact-xs" variant="subtle" color={p.active ? 'red' : 'green'} onClick={() => open(p, p.active ? 'wstrzymaj' : 'wznow')}>
                  {p.active ? 'Wstrzymaj' : 'Wznów'}
                </Button>
              </Stack>
            )}
          </Group>
        </Card>
      ))}
      {!card.plans.length && <EmptyState compact title="Brak planu przeglądów" />}
      {card.can.plans && card.device.status === 'czynne' && (
        <Button size="xs" variant="light" w="fit-content" leftSection={<IconPlus size={14} />} onClick={() => setCreating(true)}>
          Dodaj plan przeglądów
        </Button>
      )}
      <Modal
        opened={!!act || creating}
        onClose={() => (setAct(null), setCreating(false))}
        title={creating ? 'Nowy plan przeglądów' : act?.action === 'przesun' ? 'Przesunięcie terminu' : act?.action === 'cykl' ? 'Zmiana cyklu' : act?.action === 'wstrzymaj' ? 'Wstrzymanie planu' : 'Wznowienie planu'}
      >
        <Stack>
          {creating && (
            <>
              {cycleFields}
              <TextInput type="date" label="Liczony od (uruchomienie / ostatni przegląd)" value={f.baseDate} onChange={(e) => setF({ ...f, baseDate: e.currentTarget.value })} />
            </>
          )}
          {act?.action === 'cykl' && (
            <>
              {cycleFields}
              <Text size="xs" c="dimmed">
                Zmiana cyklu przelicza tylko przyszły termin.
              </Text>
            </>
          )}
          {act?.action === 'przesun' && (
            <>
              <TextInput type="date" label="Nowy termin" value={f.due} onChange={(e) => setF({ ...f, due: e.currentTarget.value })} />
              <Checkbox label="Przesuń cały harmonogram (kolejne terminy też)" checked={f.whole} onChange={(e) => setF({ ...f, whole: e.currentTarget.checked })} />
            </>
          )}
          {!creating && <TextInput label="Powód" required description={minHint(f.reason)} value={f.reason} onChange={(e) => setF({ ...f, reason: e.currentTarget.value })} />}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => (setAct(null), setCreating(false))}>
              Anuluj
            </Button>
            <Button color={!creating && act?.action === 'wstrzymaj' ? 'red' : undefined} loading={run.isPending} disabled={!creating && f.reason.trim().length < 3} onClick={() => run.mutate()}>
              {creating ? 'Dodaj plan przeglądów' : act?.action === 'przesun' ? 'Przesuń termin' : act?.action === 'cykl' ? 'Zmień cykl' : act?.action === 'wstrzymaj' ? 'Wstrzymaj plan' : 'Wznów plan'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
